import BigNumber from 'bignumber.js';
import {
  BundlePaymentPlan,
  IGetSpendingsParams,
  ISubscriptionsItem,
  Web3Address,
} from 'multirpc-sdk';
import { createSelector } from '@reduxjs/toolkit';

import { AccountPermission } from 'modules/permissions/constants';
import { RootState } from 'store';
import { ZERO } from 'modules/common/constants/const';
import { fetchBundlePaymentPlans } from 'domains/account/actions/bundles/fetchBundlePaymentPlans';
import { fetchMyBundles } from 'modules/billing/actions/fetchMyBundles';
import { fetchMySubscriptions } from 'domains/account/actions/subscriptions/fetchMySubscriptions';
import { isPackagePlan } from 'domains/account/utils/isPackagePlan';
import { selectAccountPermissions } from 'modules/account/store/selectors';
import { selectIsFreemium } from 'modules/accountStatus/store/selectors';
import { selectIsWeb3OAuth } from 'modules/user/store/selectors';
import { selectPAYGBalance } from 'modules/billing/store/selectors';
import { fetchUserSpendings } from 'domains/account/actions/fetchUserSpendings';

import { getPremiumActivationThreshold } from '../utils/getPremiumActivationThreshold';
import { mapSpendingsToChartData } from '../utils/mapSpendingsToChartData';
import {
  calculateAverageSpendingCredits,
  calculateAverageSpendingUsd,
  calculateTotalSpending,
  calculateTotalSpendingCredits,
  getSpendingItemsCount,
  isAverageHidden,
} from '../utils/spendingCalculations';

const selectBundlePaymentPlansState = createSelector(
  fetchBundlePaymentPlans.select(),
  state => state,
);

const selectBundlePaymentPlans = createSelector(
  selectBundlePaymentPlansState,
  ({ data = [] }) =>
    data.map<BundlePaymentPlan>(bundle => ({
      ...bundle,
      price: {
        ...bundle.price,
        amount: (Number(bundle.price.amount) || 0).toString(),
      },
    })),
);

const selectPaidBundlePaymentPlans = createSelector(
  selectBundlePaymentPlans,
  plans => plans.filter(plan => plan.bundle.type === 'PAID'),
);

export const selectBundlePaymentPlansInitLoading = createSelector(
  selectBundlePaymentPlansState,
  ({ data, isLoading }) => isLoading && typeof data === 'undefined',
);

export const selectBundlePaymentPlanByPriceId = createSelector(
  selectPaidBundlePaymentPlans,
  (_state: RootState, priceId?: string) => priceId,
  (bundles, priceId) =>
    bundles.find(({ bundle }) => bundle.price_id === priceId),
);

const selectMyBundlesState = createSelector(
  (state: RootState, group: Web3Address | undefined) =>
    fetchMyBundles.select({ group })(state),
  state => state,
);

const selectMyBundles = createSelector(
  selectMyBundlesState,
  ({ data }) => data || [],
);

const selectMyCurrentBundle = createSelector(
  selectMyBundles,
  (bundles): ISubscriptionsItem | undefined => bundles[0],
);

const selectMySubscriptionsState = createSelector(
  fetchMySubscriptions.select({}),
  state => state,
);

export const selectMySubscriptionsInitLoading = createSelector(
  selectMySubscriptionsState,
  ({ data, isLoading }) => isLoading && typeof data === 'undefined',
);

export const selectMySubscriptions = createSelector(
  selectMySubscriptionsState,
  ({ data }) => data?.items || [],
);

export const selectMyRecurringPayments = createSelector(
  selectMyBundles,
  selectMySubscriptions,
  (bundles, subscriptions) =>
    [...subscriptions, ...bundles].sort(
      (a, b) => Number(a.currentPeriodEnd) - Number(b.currentPeriodEnd),
    ),
);

export const selectMyRecurringPaymentsWithoutPackage = createSelector(
  selectMyRecurringPayments,
  selectPaidBundlePaymentPlans,
  (payments, bundlePaymentPlans) => {
    return payments.filter(payment => {
      const currentPlan = bundlePaymentPlans.find(plan => {
        return plan.bundle.price_id === payment.productPriceId;
      });

      return !isPackagePlan(currentPlan);
    });
  },
);

export const selectAllRecurringPaymentsAmount = createSelector(
  selectMyRecurringPayments,
  payments =>
    payments
      .reduce((amount, payment) => amount.plus(payment.amount), ZERO)
      .toString(),
);

export const selectHasRecurringPayments = createSelector(
  selectMyRecurringPayments,
  payments => payments.length > 0,
);

const freeToPremiumThreshold = getPremiumActivationThreshold();

export const selectHasFreemiumToPremiumTransition = createSelector(
  (state: RootState, _token: string, group: Web3Address | undefined) =>
    selectPAYGBalance(state, group).credits,
  selectMyCurrentBundle,
  (state: RootState, userEndpointToken: string) =>
    selectIsFreemium(state, { userEndpointToken }),
  (state: RootState) => selectIsWeb3OAuth(state),
  // eslint-disable-next-line max-params
  (balance, myBundle, hasFreemium, isWeb3UserWithEmailBound) => {
    const hasEnoughBalance = new BigNumber(balance).isGreaterThanOrEqualTo(
      freeToPremiumThreshold,
    );

    const hasBundle = Boolean(myBundle);
    const shouldBePremium = hasEnoughBalance || hasBundle;
    const hasNoEmailBound = !isWeb3UserWithEmailBound;

    return hasNoEmailBound && hasFreemium && shouldBePremium;
  },
);

export const selectHasAccountStatusTransition = createSelector(
  selectHasFreemiumToPremiumTransition,
  (state: RootState) => selectAccountPermissions(state),
  (hasFreeToPremiumTransition, accountPermissions) => {
    const hasAccess = accountPermissions.includes(
      AccountPermission.StatusTransition,
    );

    return hasAccess && hasFreeToPremiumTransition;
  },
);

/* SPENDING */
export const selectUserSpendingsData = (
  state: RootState,
  params: IGetSpendingsParams,
) => fetchUserSpendings.select(params)(state).data;

const selectTotalSpendingCreditsAmounts = createSelector(
  [selectUserSpendingsData],
  spendingData => {
    return calculateTotalSpendingCredits(spendingData);
  },
);

const selectSpendingItemsCount = createSelector(
  [selectUserSpendingsData],
  spendingData => {
    return getSpendingItemsCount(spendingData);
  },
);

export const selectIsAverageHidden = createSelector(
  [selectSpendingItemsCount],
  count => isAverageHidden(count),
);

const selectAverageSpendingCredits = createSelector(
  [selectTotalSpendingCreditsAmounts, selectSpendingItemsCount],
  (totalCreditsSpending, count) => {
    return calculateAverageSpendingCredits(totalCreditsSpending, count);
  },
);

const selectAverageSpendingUsd = createSelector(
  [selectAverageSpendingCredits],
  averageSpendingCredits => {
    return calculateAverageSpendingUsd(averageSpendingCredits);
  },
);

export const selectUserSpendingChartData = createSelector(
  [selectUserSpendingsData, selectAverageSpendingUsd],
  (spendingData, averageSpendingInUsd) => {
    const totalAverage =
      (averageSpendingInUsd.payg || 0) + (averageSpendingInUsd.bundles || 0);

    return mapSpendingsToChartData(spendingData, totalAverage);
  },
);

export const selectTotalSpending = createSelector(
  [selectTotalSpendingCreditsAmounts],
  totalCreditsSpending => {
    return calculateTotalSpending(totalCreditsSpending);
  },
);
